"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Anchor,
  ArrowRight,
  Award,
  BookOpenCheck,
  Briefcase,
  Building2,
  ClipboardList,
  Compass,
  FileSearch,
  GraduationCap,
  Landmark,
  MessageSquare,
  Route,
  Scale,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import type { PublicStats } from "@/lib/api/content";
import { FAQ_ITEMS } from "./faq-data";

const REGISTER_HREF = "/register?role=shore";

// ─── Copy blocks ─────────────────────────────────────────────────────────────

const TRANSITION_BARRIERS: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "The translation problem",
    body: "Your CV says 'Chief Officer, 14 months on VLCCs'. The hiring manager ashore reads ranks and tonnage and learns nothing. What they shortlist by — cargo planning for high-value consignments, vetting inspection readiness, multinational crew leadership, ISM compliance — is exactly what you did, in words you were never taught to use. The experience transfers. The language does not, unless someone translates it.",
    Icon: FileSearch,
  },
  {
    title: "Not knowing which roles actually fit",
    body: "Ask ten people what a chief engineer can do ashore and you get two answers — superintendent or teaching — plus eight shrugs. The real market is wider: fleet performance, planned maintenance, newbuilding supervision, surveying, reliability, technical procurement. Nobody hands you the map, so most officers either aim at the two roles everyone names, or give up and sign on for another contract.",
    Icon: Compass,
  },
  {
    title: "Employers who cannot read sea time",
    body: "Shore hiring teams routinely undervalue sea-going experience because it arrives unlabelled. Twelve years of incident command, audit exposure and machinery diagnostics looks like 'worked on ships' when it is presented as a list of vessels and dates. The candidate loses the shortlist; the employer loses the best operator they never interviewed. Both sides are sure the other one failed.",
    Icon: Building2,
  },
  {
    title: "Advice from people selling something",
    body: "The guidance that does exist usually comes with an invoice attached: a generic MBA, a resume-writing service, a 'maritime career coach' who has never stood a watch. What a transitioning officer needs is not motivation — it is a precise answer to a precise question: for this role, what do I already have, what am I missing, and what closes it?",
    Icon: Scale,
  },
];

const ADVISOR_FEATURES: { title: string; body: string; Icon: LucideIcon }[] = [
  {
    title: "Readiness scores against real roles",
    body: "Choose up to six target shore positions — marine superintendent, port operations manager, crewing manager, vetting inspector, HSQE advisor, technical superintendent and more. For each one you get a live readiness score calculated from your rank, sea service, vessel types, certificates and shore experience. Not a personality test: a structured assessment against the platform's role qualification rules.",
    Icon: TrendingUp,
  },
  {
    title: "Transferable strengths, named explicitly",
    body: "For every target role, the analysis lists what your background already covers — in the language shore employers use. Cargo planning and stability work becomes terminal and stowage operations. Planned maintenance becomes reliability management. ISM compliance and audit readiness become SMS ownership. This is the translation layer, done for you, and it doubles as the vocabulary for your CV and your interviews.",
    Icon: Award,
  },
  {
    title: "Gaps named, courses matched",
    body: "You also see what is missing — terminal management systems, fleet budgeting, commercial contracts, shore-side regulatory reporting — each gap tied to the specific target role it blocks. Where a gap is closeable with training, the platform points at real courses from verified maritime institutes, with dates, locations and seats. You invest in the gap that matters, not a generic degree.",
    Icon: GraduationCap,
  },
  {
    title: "Honest barriers, cited sources",
    body: "Some targets carry hard requirements — a surveyor's statutory accreditation, a DPA role's documented SMS experience. The Transition Advisor flags these up front and shows the basis every assessment is built on: role qualification rules, industry frameworks and regulatory requirements. You find out what genuinely blocks you before you spend a year and a tuition fee discovering it.",
    Icon: ShieldCheck,
  },
];

const HOW_IT_WORKS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "Build the profile that carries your history",
    body: "Rank and sea service if you sailed — vessel types, certificates, flag endorsements — plus your shore roles, disciplines and qualifications if you are already ashore. Fifteen minutes of structured fields replaces every CV version you have ever maintained, and every field is something employers can filter by.",
  },
  {
    step: "02",
    title: "Pick your target shore roles",
    body: "Choose up to six positions from the platform's mapped shore catalogue — marine superintendent, port operations manager, crewing manager, fleet performance officer, vetting inspector, HSQE advisor and more. These are the roles the analysis will score you against, and you can change them whenever your plans change.",
  },
  {
    step: "03",
    title: "Read your readiness, not your horoscope",
    body: "For each target you get a readiness score, the strengths that transfer stated in shore language, the gaps that block you stated by name, and course recommendations that close them. Hard barriers — statutory accreditations, documented experience requirements — are flagged honestly, with the sources cited.",
  },
  {
    step: "04",
    title: "Close the gap, then take the role",
    body: "Apply to verified employers with your structured profile, message them directly inside the platform, and track every application in one place. As you add certificates, courses and shore experience, your readiness scores move — so the plan updates as you execute it.",
  },
];

const TRANSITION_PATHS: {
  from: string;
  to: string;
  transfers: string[];
  gaps: string[];
}[] = [
  {
    from: "Master",
    to: "Marine Superintendent / Marine Manager",
    transfers: ["ISM & ISPS compliance", "Incident command", "Vetting & audit readiness", "Crew leadership"],
    gaps: ["Fleet-scale SMS ownership", "Regulatory reporting", "Budget accountability"],
  },
  {
    from: "Chief Engineer",
    to: "Technical Superintendent",
    transfers: ["Planned maintenance systems", "Machinery diagnostics", "Dry-dock specification & supervision", "Spares & inventory control"],
    gaps: ["Fleet budgeting", "Multi-vessel oversight", "Contractor negotiation"],
  },
  {
    from: "Chief Officer",
    to: "Port / Terminal Operations Manager",
    transfers: ["Cargo planning & stability", "Stowage & securing", "Port interface & documentation", "Stevedore coordination"],
    gaps: ["Terminal operating systems", "Commercial contracts", "Shore labour frameworks"],
  },
  {
    from: "Second Engineer",
    to: "Fleet Performance / Planned Maintenance Officer",
    transfers: ["Condition monitoring", "Fuel & emissions data", "Maintenance scheduling", "Defect analysis"],
    gaps: ["Fleet analytics tooling", "KPI reporting", "Excel-to-database jump"],
  },
  {
    from: "Senior Deck Officer",
    to: "Vetting Inspector / Marine Surveyor",
    transfers: ["SIRE & CDI exposure", "Cargo & safety systems", "Defect rectification", "Port state interaction"],
    gaps: ["Statutory accreditation", "Report writing at standard", "Surveyor ethics frameworks"],
  },
  {
    from: "Any Senior Officer",
    to: "Crewing Manager",
    transfers: ["Certificate & flag-state knowledge", "Crew performance judgement", "Rotation & relief planning", "Welfare realities"],
    gaps: ["HR systems & labour law", "Recruitment pipeline metrics", "Vendor management"],
  },
];

const TARGET_DOMAINS: { name: string; body: string; Icon: LucideIcon }[] = [
  {
    name: "Technical & fleet management",
    body: "Technical superintendent, fleet performance, planned maintenance, newbuilding supervision, dry-dock project roles. This is the largest destination for senior engineers, and the one where machinery-specific sea time — dual-fuel plants, tanker cargo systems, DP — commands a genuine premium.",
    Icon: ClipboardList,
  },
  {
    name: "Marine operations & compliance",
    body: "Marine superintendent, DPA, HSQE advisor, vetting inspector, marine surveyor, port captain. Roles where command experience is close to a prerequisite, and where the transition gap is usually audit methodology and shore-side documentation rather than anything you did at sea.",
    Icon: ShieldCheck,
  },
  {
    name: "Port & terminal operations",
    body: "Port operations manager, terminal planner, berth coordinator, marine services and harbour roles. Deck officers arrive with cargo planning, stability and port-interface experience that terminal employers struggle to find anywhere else — the transfer is direct and the demand is steady.",
    Icon: Landmark,
  },
  {
    name: "Crewing & marine HR",
    body: "Crewing manager, crew operations executive, marine HR, training and competency roles. Officers who have been on the receiving end of crewing decisions bring judgement about certificates, rotations and welfare that pure-HR hires take years to build.",
    Icon: Briefcase,
  },
  {
    name: "Commercial & chartering",
    body: "Operations executive, post-fixture, chartering, claims and demurrage. The steepest transition on this list — the sea-time transfer is real but thinner, and the gap (contracts, commercial arithmetic, market exposure) is wider. Exactly the case where an honest skill-gap analysis saves you from a blind leap.",
    Icon: TrendingUp,
  },
  {
    name: "Training & assessment",
    body: "Maritime faculty, simulator instructor, assessor, training superintendent. Senior officers with a teaching bent move here deliberately, and institutes on the platform post faculty requirements alongside their courses — so the same profile that maps your readiness can surface the vacancy.",
    Icon: GraduationCap,
  },
];

const SHORE_SIDE_FEATURES: { title: string; body: string }[] = [
  {
    title: "Structured profiles, not CV attachments",
    body: "Your shore discipline, vessel-type exposure, fleet scale and certifications are fields an employer can filter — not bullet points buried in a PDF. When a ship manager needs a technical superintendent with gas carrier background, structured data is what gets you into the shortlist.",
  },
  {
    title: "Progression intelligence for shore-to-shore moves",
    body: "The same engine that maps sea-to-shore transitions maps your next shore move. Set fleet manager, marine manager or general manager as a target and see what your superintendent years already cover — and what the step up actually requires. Careers ashore drift sideways just as easily as careers at sea; the fix is the same map.",
  },
  {
    title: "Direct access, no recruiter filter",
    body: "Apply to verified maritime employers and message them inside the platform. No agency deciding which CVs get forwarded, no black hole, no one taking a margin on your salary negotiation. The conversation about your next role happens between you and the employer.",
  },
];

// ─── Motion helpers ──────────────────────────────────────────────────────────

function useInViewOnce(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options, inView]);

  return [ref, inView] as const;
}

function useCountUp(target: number, inView: boolean, reduced: boolean) {
  const [value, setValue] = useState(reduced ? target : 0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    let start: number | undefined;
    const duration = 1400;
    let raf: number;

    const step = (ts: number) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, inView, reduced]);

  return value;
}

function LiveStat({ value, label }: { value: number; label: string }) {
  const [ref, inView] = useInViewOnce({ threshold: 0.3, rootMargin: "-60px" });
  const reduced = useReducedMotion();
  const display = useCountUp(value, inView, !!reduced);

  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display text-3xl font-bold text-white sm:text-4xl">
        {display.toLocaleString("en-US")}
      </span>
      <span className="mt-1 text-sm text-ash">{label}</span>
    </div>
  );
}

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Hero animated element: the crossing ─────────────────────────────────────
// A vessel leaves open water on the left, crosses a plotted track, and arrives
// at the shoreline skyline on the right. Waypoints mark the transition stages.
// Reduced motion: static track, vessel at the quay.

const CROSSING_PATH = "M 70 300 C 170 285, 250 250, 330 205 S 450 130, 505 105";

const CROSSING_WAYPOINTS = [
  { x: 70, y: 300, label: "SEA SERVICE", tone: "cyan" },
  { x: 330, y: 205, label: "SKILL-GAP ANALYSIS", tone: "purple" },
  { x: 505, y: 105, label: "SHORE ROLE", tone: "green" },
] as const;

function CrossingPlot() {
  const reduced = useReducedMotion();
  const last = CROSSING_WAYPOINTS[CROSSING_WAYPOINTS.length - 1];

  return (
    <div className="relative rounded-[2rem] border border-neon-cyan/15 bg-panel/70 p-4 shadow-2xl shadow-neon-cyan/10 backdrop-blur">
      <div className="rounded-[1.5rem] border border-neon-cyan/10 bg-void p-4 sm:p-5">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon-cyan">
            The crossing · sea → shore
          </p>
          <p className="hidden font-mono text-[11px] text-ash sm:block">
            PLT CA-201 · ONE WAY
          </p>
        </div>

        <svg
          viewBox="0 0 560 400"
          role="img"
          aria-label="A vessel crossing from open water to a shoreline city, along a plotted track with waypoints labelled Sea Service, Skill-Gap Analysis and Shore Role."
          className="mt-3 h-auto w-full"
        >
          {/* open water — layered wave contours, left */}
          <g stroke="#00f3ff" strokeOpacity="0.14" strokeWidth="1.2" fill="none">
            <path d="M 10 340 q 25 -10 50 0 t 50 0 t 50 0 t 50 0" />
            <path d="M 20 362 q 25 -10 50 0 t 50 0 t 50 0 t 50 0" />
            <path d="M 10 384 q 25 -10 50 0 t 50 0 t 50 0 t 50 0" />
          </g>
          <text x="16" y="326" fill="#6f7488" fontSize="9" fontFamily="var(--font-mono)">
            OPEN WATER
          </text>

          {/* shoreline + skyline, right */}
          <g>
            <path
              d="M 420 400 L 420 210 L 448 210 L 448 168 L 478 168 L 478 210 L 500 210 L 500 142 L 528 142 L 528 210 L 556 210 L 556 400 Z"
              fill="#a855f7"
              fillOpacity="0.08"
              stroke="#a855f7"
              strokeOpacity="0.4"
              strokeWidth="1.2"
            />
            <g fill="#a855f7" fillOpacity="0.5">
              <rect x="430" y="222" width="5" height="5" />
              <rect x="458" y="182" width="5" height="5" />
              <rect x="510" y="156" width="5" height="5" />
              <rect x="510" y="176" width="5" height="5" />
              <rect x="536" y="222" width="5" height="5" />
            </g>
            <text x="486" y="66" fill="#a855f7" fillOpacity="0.8" fontSize="9" fontFamily="var(--font-mono)" textAnchor="middle">
              SHORE
            </text>
          </g>

          {/* the crossing track */}
          <motion.path
            d={CROSSING_PATH}
            fill="none"
            stroke="#00f3ff"
            strokeWidth="2"
            strokeDasharray="7 6"
            strokeLinecap="round"
            initial={reduced ? { pathLength: 1 } : { pathLength: 0 }}
            animate={reduced ? undefined : { pathLength: 1 }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
          />

          {/* waypoints */}
          {CROSSING_WAYPOINTS.map((wp) => {
            const color =
              wp.tone === "green" ? "#00ff9d" : wp.tone === "purple" ? "#a855f7" : "#00f3ff";
            return (
              <g key={wp.label}>
                <circle cx={wp.x} cy={wp.y} r="7" fill="none" stroke={color} strokeWidth="1.6" />
                <circle cx={wp.x} cy={wp.y} r="2.4" fill={color} />
                <text
                  x={wp.x + 12}
                  y={wp.y + 4}
                  fill={wp.tone === "cyan" ? "#e5e7ef" : color}
                  fontSize="10.5"
                  fontWeight="700"
                  fontFamily="var(--font-mono)"
                >
                  {wp.label}
                </text>
              </g>
            );
          })}

          {/* vessel marker */}
          {reduced ? (
            <g transform={`translate(${last.x} ${last.y})`}>
              <path d="M 0 -9 L 6 8 L 0 4 L -6 8 Z" fill="#00ff9d" />
            </g>
          ) : (
            <g>
              <path d="M 0 -9 L 6 8 L 0 4 L -6 8 Z" fill="#00f3ff">
                <animateMotion
                  dur="8s"
                  repeatCount="indefinite"
                  rotate="auto"
                  path={CROSSING_PATH}
                />
              </path>
            </g>
          )}
        </svg>

        <div className="mt-3 grid gap-2 border-t border-white/10 pt-3 font-mono text-[11px] text-ash sm:grid-cols-3">
          <p>
            <span className="text-neon-cyan">DEP</span> last port of discharge
          </p>
          <p>
            <span className="text-neon-purple">XTE</span> closing, on track
          </p>
          <p>
            <span className="text-neon-green">ARR</span> a desk that needs your sea time
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── CTA blocks ──────────────────────────────────────────────────────────────

function PrimaryCta({ large = false }: { large?: boolean }) {
  return (
    <Link
      href={REGISTER_HREF}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-cyan font-bold text-obsidian shadow-glow-cyan transition hover:scale-[1.01] hover:bg-neon-cyan/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian ${
        large ? "px-8 py-4 text-base" : "px-7 py-3.5 text-sm"
      }`}
    >
      <Route className={large ? "h-5 w-5" : "h-4 w-4"} aria-hidden="true" />
      Get your free skill-gap analysis
    </Link>
  );
}

function SignInLink({ className = "" }: { className?: string }) {
  return (
    <p className={`text-sm text-ash ${className}`}>
      Already have an account?{" "}
      <Link
        href="/login"
        className="font-semibold text-neon-cyan transition-colors hover:text-neon-cyan/80"
      >
        Sign in
      </Link>
    </p>
  );
}

function MidPageCta() {
  return (
    <Reveal className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-5 rounded-[2rem] border border-neon-purple/15 bg-panel p-8 text-center md:flex-row md:justify-between md:text-left">
        <div>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold text-neon-purple md:justify-start">
            <Compass className="h-4 w-4" aria-hidden="true" />
            Free for maritime professionals. Always.
          </p>
          <p className="mt-2 text-lg font-bold text-white">
            Fifteen minutes of profile, one click of analysis — and the
            transition stops being a guess.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 md:items-end">
          <PrimaryCta />
          <SignInLink />
        </div>
      </div>
    </Reveal>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ShoreCareersContent({ stats }: { stats: PublicStats | null }) {
  const prefersReducedMotion = useReducedMotion();

  const heroFade = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 18 },
    visible: { opacity: 1, y: 0 },
  };

  const liveStats = stats
    ? (
        [
          { key: "shoreProfessionals", label: "active shore professionals", value: stats.shoreProfessionals || stats.activeShoreProfessionals },
          { key: "shoreJobs", label: "open shore positions", value: stats.shoreJobs || stats.openPositions },
          { key: "shorePositions", label: "shore roles mapped", value: stats.shorePositions },
          { key: "skillGapRules", label: "skill-gap rules behind the scores", value: stats.skillGapRules },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  const advisorDepth = stats
    ? (
        [
          { label: "shore roles mapped", value: stats.shorePositions },
          { label: "with skill-gap guidance", value: stats.shorePositionsWithGuidance },
          { label: "skill-gap rules", value: stats.skillGapRules },
        ] as const
      ).filter((item) => item.value > 0)
    : [];

  return (
    <main className="min-h-screen overflow-hidden bg-obsidian text-foreground">
      <Breadcrumbs />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative px-4 pb-16 pt-10 sm:px-6 sm:pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(0,243,255,0.12),transparent_32%),radial-gradient(circle_at_85%_25%,rgba(168,85,247,0.1),transparent_34%)]"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-neon-purple/20 bg-neon-purple/8 px-4 py-2 text-sm font-semibold text-neon-purple">
              <Compass className="h-4 w-4" aria-hidden="true" />
              For transitioning seafarers &amp; shore professionals
            </div>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              Your sea time already counts. Here is the proof — and the plan.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ash">
              The question every officer asks before moving ashore is never
              &ldquo;where are the shore jobs?&rdquo; It is &ldquo;will my
              experience actually translate?&rdquo; CrewAhead answers it with
              arithmetic instead of reassurance: a skill-gap analysis that maps
              your rank, sea service and certificates to real shore roles —
              crewing, technical, commercial, compliance, port operations — and
              shows you the readiness score, the transferable strengths and the
              exact gaps to close. Built by a master mariner who watched too
              many good officers make the crossing blind.
            </p>
            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <PrimaryCta large />
                <Link
                  href="/jobs"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-neon-purple/30 bg-neon-purple/8 px-7 py-4 font-bold text-neon-purple transition hover:border-neon-purple/60"
                >
                  <Briefcase className="h-5 w-5" aria-hidden="true" />
                  Browse shore vacancies
                </Link>
              </div>
              <SignInLink />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={heroFade}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <CrossingPlot />
          </motion.div>
        </div>
      </section>

      {/* ── Live stats ───────────────────────────────────────────────────── */}
      {liveStats.length > 0 && (
        <section
          aria-label="Live platform statistics"
          className="border-y border-white/5 bg-panel/30 px-4 py-12 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-neon-green">
              Live from the platform — real counts, updated as you read this
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {liveStats.map((item) => (
                <LiveStat key={item.key} value={item.value} label={item.label} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Why the transition is hard ───────────────────────────────────── */}
      <section aria-labelledby="hard-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-magenta">
              The honest part first
            </p>
            <h2 id="hard-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Why the sea-to-shore transition defeats good officers.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              It is not a shortage of shore jobs, and it is rarely a shortage
              of ability. The transition fails in the gap between what you did
              and what employers can see — a gap made of vocabulary, visibility
              and advice that arrives with a price tag. These are the four
              failure modes, named plainly.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {TRANSITION_BARRIERS.map((barrier, i) => (
              <Reveal key={barrier.title} delay={i * 0.06}>
                <article className="group h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-magenta/25 hover:bg-panel-hover">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-magenta/15 bg-neon-magenta/8 p-2.5 transition-colors group-hover:bg-neon-magenta/12">
                      <barrier.Icon className="h-5 w-5 text-neon-magenta" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{barrier.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{barrier.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section aria-labelledby="how-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              How CrewAhead works
            </p>
            <h2 id="how-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              From sea service to shore shortlist, in four steps.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              No recruiters, no forwarded CVs, no premium tier standing between
              you and the analysis. The whole loop — profile, targets, scores,
              applications — runs inside one platform you control.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {HOW_IT_WORKS.map((item, i) => (
              <Reveal key={item.step} delay={i * 0.07}>
                <article className="h-full rounded-xl border border-white/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/25 hover:bg-panel-hover">
                  <p className="font-mono text-sm font-bold text-neon-cyan">{item.step}</p>
                  <h3 className="mt-3 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Centerpiece: Transition Advisor / skill-gap analysis ─────────── */}
      <section
        aria-labelledby="advisor-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              The core of the platform
            </p>
            <h2 id="advisor-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              The Transition Advisor: your experience, translated into
              shore-role readiness.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              This is not a personality quiz or a premium upsell — it is the
              live career intelligence engine every registered professional
              gets, and it exists because the industry never built one. Pick
              your target shore roles. The engine assesses your profile against
              the qualification rules for each role and answers the only three
              questions that matter: what you already have, what you are
              missing, and what closes the distance.
            </p>
          </div>

          {advisorDepth.length > 0 && (
            <div className="mt-10 grid gap-8 border-y border-white/10 py-10 sm:grid-cols-3">
              {advisorDepth.map((item) => (
                <LiveStat key={item.label} value={item.value} label={item.label} />
              ))}
            </div>
          )}

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {ADVISOR_FEATURES.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-cyan/10 bg-panel/70 p-6 transition-colors hover:border-neon-cyan/30 hover:bg-panel-hover">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg border border-neon-cyan/15 bg-neon-cyan/8 p-2.5">
                      <feature.Icon className="h-5 w-5 text-neon-cyan" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">{feature.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-ash">{feature.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="rounded-xl border border-neon-green/20 bg-neon-green/8 p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <BookOpenCheck className="mt-1 h-6 w-6 shrink-0 text-neon-green" aria-hidden="true" />
                <div>
                  <h3 className="text-lg font-bold text-neon-green">
                    Live today — not on a roadmap
                  </h3>
                  <p className="mt-2 max-w-3xl text-sm leading-7 text-ash">
                    The Transition Advisor runs in the dashboard of every
                    registered seafarer and shore professional right now, at{" "}
                    <span className="font-mono text-foreground">/seafarer/career</span> and{" "}
                    <span className="font-mono text-foreground">/shore/career</span>.
                    Registration is free, the analysis is free, and your first
                    readiness scores are calculated the moment your profile has
                    enough detail to assess. There is no sales call between you
                    and the answer.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Real transition paths ────────────────────────────────────────── */}
      <section aria-labelledby="paths-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-purple">
              By transition path
            </p>
            <h2 id="paths-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Real crossings, mapped. Find yours.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Abstract promises do not help you plan a mortgage, a family move
              or a resignation letter. These are the actual translations the
              analysis produces — what crosses over intact, and what you still
              need to build.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {TRANSITION_PATHS.map((path, i) => (
              <Reveal key={path.to} delay={i * 0.05}>
                <article className="h-full rounded-[1.75rem] border border-neon-purple/10 bg-panel p-6 shadow-xl shadow-black/20 transition hover:-translate-y-1 hover:border-neon-purple/30">
                  <div className="flex flex-col gap-2">
                    <span className="font-display text-sm font-bold text-neon-cyan">
                      {path.from}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-ash">
                      <Anchor className="h-3 w-3" aria-hidden="true" /> becomes
                    </span>
                    <span className="font-display text-sm font-bold text-neon-purple">
                      {path.to}
                    </span>
                  </div>
                  <div className="mt-5">
                    <p className="text-xs font-bold uppercase tracking-wider text-neon-green">
                      Transfers intact
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-ash">
                      {path.transfers.map((t) => (
                        <li key={t} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-green" />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-neon-amber">
                      Gap to close
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-ash">
                      {path.gaps.map((g) => (
                        <li key={g} className="flex items-start gap-2">
                          <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full border border-neon-amber/50" />
                          {g}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MidPageCta />

      {/* ── Target roles by domain ───────────────────────────────────────── */}
      <section
        aria-labelledby="domains-heading"
        className="border-y border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-green">
              By target role
            </p>
            <h2 id="domains-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Shore careers by discipline — where maritime experience lands.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              Shore-side maritime hiring is not one market; it is six or seven
              distinct ones with different gatekeepers, different pay logic and
              different respect for sea time. Here is how they actually differ.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TARGET_DOMAINS.map((domain, i) => (
              <Reveal key={domain.name} delay={i * 0.05}>
                <article className="h-full rounded-xl border border-neon-green/10 bg-panel/70 p-6 transition-colors hover:border-neon-green/30 hover:bg-panel-hover">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-neon-green/15 bg-neon-green/8">
                    <domain.Icon className="h-5 w-5 text-neon-green" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold">{domain.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{domain.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Already shore-side ───────────────────────────────────────────── */}
      <section aria-labelledby="shore-side-heading" className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-amber">
              Already working ashore
            </p>
            <h2 id="shore-side-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
              Not transitioning? This is still built for you.
            </h2>
            <p className="mt-5 text-lg leading-8 text-ash">
              A large share of maritime shore professionals never sailed — and
              plenty of those who did have been ashore for a decade. If you are
              in crewing, technical, commercial or compliance today, the problem
              is not translation. It is visibility and progression: the right
              employers cannot see you, and your next step is as unmappable as
              the crossing was for the seafarers. Generic job boards make it
              worse — they index shore maritime roles under the same keyword
              soup as warehouse logistics and hotel work, so a technical
              superintendent vacancy sits beside a catering supervisor ad and
              both sides learn to ignore the channel. CrewAhead fixes both
              halves: maritime-only structure for visibility, and the same
              career intelligence for progression.
            </p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {SHORE_SIDE_FEATURES.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 0.06}>
                <article className="h-full rounded-xl border border-neon-amber/15 bg-panel/70 p-6 transition-colors hover:border-neon-amber/35 hover:bg-panel-hover">
                  <h3 className="text-base font-semibold text-neon-amber">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-ash">{feature.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="max-w-3xl text-sm leading-7 text-ash">
              Curious why a platform built around careers, not listings, exists
              at all?{" "}
              <Link href="/about" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                Read the mission
              </Link>{" "}
              — or browse the{" "}
              <Link href="/faq" className="font-semibold text-neon-cyan hover:text-neon-cyan/80">
                platform FAQ
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section
        aria-labelledby="faq-heading"
        className="border-t border-white/5 bg-panel/30 px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-4xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-neon-cyan">
              By concern
            </p>
            <h2 id="faq-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Questions people ask before making the crossing.
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, i) => (
              <Reveal key={item.question} delay={i * 0.04}>
                <details className="group rounded-xl border border-white/10 bg-panel/70 p-5 transition-colors open:border-neon-cyan/25 hover:border-neon-cyan/20">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
                    {item.question}
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-neon-cyan transition-transform group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-7 text-ash">{item.answer}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-neon-cyan/15 bg-panel p-8 sm:p-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-sm font-semibold text-neon-cyan">
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
                Stop guessing. Start mapping.
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
                The crossing is a decision you make once. Make it with the map.
              </h2>
              <p className="mt-4 text-base leading-7 text-ash">
                Register free, build your profile, pick your target roles — and
                see your readiness scores tonight. Whether you are six months
                from leaving the sea or six years into a shore desk, the
                analysis meets you where you are.
              </p>
            </div>
            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-center">
              <PrimaryCta large />
              <SignInLink className="text-center" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
